import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000BasicTable, { type U2000SearchPayload, type U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000IdCheckedBoxDisplay } from "../../u2000Core/components/tables/display/U2000IdCheckedBoxDisplay";
import { U2000IdHeaderCheckedBoxDisplay } from "../../u2000Core/components/tables/display/U2000IdHeaderCheckedBoxDisplay";
import type { U2000TableColumn } from "../../u2000Core/components/tables/basic/U2000BasicTableList";
import React, { useEffect, useRef } from "react";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import { useU2000QuickSearchListEffect } from "../../u2000Core/hooks/useU2000QuickSearchListEffect";
import { useNavigate } from "react-router-dom";
import type { TFunction } from "i18next";

import type { U2000TableHandlers } from "../../u2000Core/types/U2000TableHandlers";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextPageAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { useU3141Context } from "../U3141Context";
import { doDeleteData, doExportExcel, doSearch, luHandleAppiError, type U3141MasterLotChain } from "../U3141Service";
import { useU2000ExportExcelHandlers } from "../../u2000Core/hooks/handler/U2000UseExportExcelHandlers";
import { useU2000ExportExcelNotification } from "../../u2000Core/hooks/handler/U2000UseExportExcelNotification";
import { useU3141LandRegisterListNotification } from "./list/U3141UseLandRegisterListNotification";
import { NS, U3141ROLES } from "../U3141";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import U3141LandRegisterDeleteDlg from "./common/U3141LandRegisterDeleteDlg";
import { U3141LandRegisterListToolBar } from "./list/U3141LandRegisterListToolBar";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { u2000BuildQuickSearchFormPath, u2000ShouldQuickSearchOpenForm } from "../../u2000Core/tools/U2000QuickSearchUtils";
import {
  U2000_CRITERIA_LOOKUP_U3141_FIND_PROJECT,
  defaultItemToDisplay,
  defaultItemToValue,
  registerCriteriaLookup,
  unregisterCriteriaLookup,
} from "../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry";
import { useU3141FindProjectHandlers } from "../U3141UseFindProjectHandlers";

export function useU3141LandRegisterListHandlerActions<T>(handlers: U2000TableHandlers<T>): U2000TableHandlers<T> {
  return handlers;
}

// Function to get columns definition
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    {
      name: "id",
      label: "",
      widthClass: "w-[50px]",
      type: "number",
      align: "center",
      headerDisplay: (column: any, label: string, allRowSelected: boolean, handleSelectAllRow?: (isSelected: boolean) => void) => {
        return <U2000IdHeaderCheckedBoxDisplay column={column} label={label} disabled={false} allRowSelected={allRowSelected} handleSelectAllRow={handleSelectAllRow} />;
      },
      display: (column: any, value: any, row: any, allRowSelected: boolean, rowUpdated?: (row: any) => void) => {
        return <U2000IdCheckedBoxDisplay column={column} value={value} row={row} disabled={false} rowUpdated={rowUpdated} allowRowSelection={allRowSelected} />;
      },
      isCriteriaField: false,
    },
    {
      name: "projectNumber",
      label: t("lblProject", { ns: NS }),
      isDefaultColumn: true,
      isDefaultSortedColumn: false,
      type: "string",
      align: "left",
      inGridField: true,
      sortable: true,
      includeConditions: ["equals", "in"],
      isCriteriaField: true,
      criteriaLookup: {
        findMethod: U2000_CRITERIA_LOOKUP_U3141_FIND_PROJECT,
        searchCriterionField: "projetNumber",
        valueKey: "projetNumber",
        criteriaField: "projectNumber",
        displayKeys: ["projetNumber", "projetName"],
        placeholder: t("phCriteriaProject", { ns: "common" }),
      },
    },
    {
      name: "projectDescription",
      label: t("lblCndProjectDescription", { ns: NS }),
      isDefaultColumn: true,
      isDefaultSortedColumn: false,
      type: "string",
      align: "left",
      inGridField: true,
      excludeConditions: [],
      isCriteriaField: true,
    },
    {
      name: "requestedLot",
      label: t("lblCndRequestedLot", { ns: NS }),
      isDefaultColumn: true,
      isDefaultSortedColumn: false,
      type: "string",
      align: "left",
      inGridField: true,
      sortable: true,
      excludeConditions: [],
      isCriteriaField: true,
    },
    {
      name: "createdAt",
      label: t("lblFldCreatedAt", { ns: NS }),
      widthClass: "w-[150px]",
      isDefaultColumn: true,
      isDefaultSortedColumn: false,
      type: "date",
      align: "left",
      inGridField: true,
      sortable: true,
      excludeConditions: [],
      includeConditions: ["equals", "notequals", "greaterthan", "greaterthanorequal", "lesthan", "lesthanorequal", "interval"],
      isCriteriaField: true,
    },
  ];
}

const U3141LandRegisterList: React.FC = () => {
  // Navigation
  const navigate = useNavigate();

  // Translation
  const { t, i18n } = useTranslation();

  // Toast
  const toast = useToast();

  // Auth context for role checking
  const { hasRole } = useU2000AuthContext();

  // Search Handlers, Grid Handlers from Context
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3141Context();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);
  const findProjectHandlers = useU3141FindProjectHandlers();

  useEffect(() => {
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3141_FIND_PROJECT, {
      search: findProjectHandlers.search,
      itemToValue: defaultItemToValue,
      itemToDisplay: defaultItemToDisplay,
    });
    return () => unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3141_FIND_PROJECT);
  }, [findProjectHandlers.search]);

  // Initialize columns on mount or when 't' changes
  useEffect(() => {
    searchStateHandlers.setColumns(getColumnsDef(t));
    gridStateHandlers.setColumns(getColumnsDef(t));
    setLogicalUnitBreadcrumb([{ label: t("title", { ns: NS }) }]);
  }, [t]);

  // Manage Handler Actions
  const u3141LandRegisterListHandlerActions: U2000TableHandlers<U3141MasterLotChain> = useU3141LandRegisterListHandlerActions<U3141MasterLotChain>({
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },
    onRowClick: (landRegister: U3141MasterLotChain) => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
      luStateHandlers.setActionOriginFrom("");
      const currentIdx = gridStateHandlers ? gridStateHandlers.getData().findIndex((r) => r.id === landRegister.id) : -1;
      if (gridStateHandlers) {
        gridStateHandlers.setCurrentIdx(currentIdx);
      }
      navigate(`${landRegister.id}`);
    },
    onAdd: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.ADD);
      luStateHandlers.setActionOriginFrom("/U3141");
      navigate("new");
    },
    onModify: () => {
      luStateHandlers.setNextAction(U2000LUStateEnumNextAction.MODIFY);
      luStateHandlers.setActionOriginFrom("/U3141");
      navigate(`${gridStateHandlers.getSelectedRows()[0]?.id}`);
    },
    onDelete: () => {
      handleDelete.handleDelete();
    },
    onExportExcel: async () => {
      const filter = searchStateHandlers.extractSearchParameter();
      await handleExportExcel.handleExportExcel(filter);
    },
  });

  // Call Search Handlers for Logical Unit
  const handleSearch = useU2000SearchHandlers<U3141MasterLotChain>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      return await doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3141MasterLotChain>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());

      const qsSession = consumeQsSessionRef.current();
      const isFromQuickSearch = qsSession?.isFromQuickSearch === true;
      const targetLU = qsSession?.targetLogicalUnit ?? "/U3141";
      if (
        u2000ShouldQuickSearchOpenForm(window.location.pathname, targetLU) &&
        searchResult.data &&
        searchResult.data.length === 1 &&
        isFromQuickSearch
      ) {
        const singleLandRegister = searchResult.data[0];
        luStateHandlers.setNextAction(U2000LUStateEnumNextAction.NONE);
        luStateHandlers.setActionOriginFrom("");
        const currentIdx = gridStateHandlers.getData().findIndex((r) => r.id === singleLandRegister.id);
        if (currentIdx !== -1) {
          gridStateHandlers.setCurrentIdx(currentIdx);
        }
        navigate(u2000BuildQuickSearchFormPath(targetLU, singleLandRegister.id), { replace: true });
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Call Export Excel Handlers for Logical Unit
  const handleExportExcel = useU2000ExportExcelHandlers({
    doExportExcelFn: async (filter, lang) => {
      return await doExportExcel(u2000AxiosAuthenticated, filter, lang);
    },
    i18nLanguage: i18n.language,
    onSuccess: (processId: string) => {
      toast.success(
        t("msgExportExcelStarted", { processId, ns: "common" }) || `Export en cours (référence: ${processId})`,
        t("successTitle", { ns: "common" })
      );
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  // Écouter les notifications WebSocket pour les exports Excel terminés
  useU2000ExportExcelNotification();

  // Refresh de la liste quand l'export Excel U3141 est terminé (notification:new)
  useU3141LandRegisterListNotification(() => {
    handleSearch.handleSearch(
      searchStateHandlers.extractSearchParameter(),
      gridStateHandlers.extractPagination(gridStateHandlers.getCurrentPage()),
      U2000GridStateEnumPaginationDirection.NEXTPAGE
    );
  });

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: "/U3141",
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  // Gestion des Delete Record avec confirmation
  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => (gridStateHandlers ? gridStateHandlers.getSelectedRows() : []),
    doDeleteFn: async (id, row) => {
      return await doDeleteData(u2000AxiosAuthenticated, id, row);
    },

    onSuccess: (deletedRows) => {
      const page = gridStateHandlers.getCurrentPage();
      let newData = gridStateHandlers.getData();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: row["projectNumber"], ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== row?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      // Après suppression, aller au prochain enregistrement, aller vers l'avant si possible, sinon vers l'arrière, si aucun, retourner à la liste
      const action: U2000GridStateNextPageAfterDeleteReturn = gridStateHandlers.getNextPageAfterDelete(page, totalRows);
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(action.page), U2000GridStateEnumPaginationDirection.NEXTPAGE);
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  // s'assurer que les colonnes sont chargées avant le rendu car creer des problemes d'affichage avec les columnes par defaut si searchStateHandlers.columns est vide au rendu
  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  // Render
  return (
    <>
      <U3141LandRegisterDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />

      <U2000BasicTable
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        actionsButtons={<U3141LandRegisterListToolBar gridStateHandlers={gridStateHandlers as any} handlerActions={u3141LandRegisterListHandlerActions as any} />}
        handlerActions={u3141LandRegisterListHandlerActions}
        label={t("lblLogicalUnit", {
          count: gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
        allowRowSelection={true}
        exportExcelTitle={t("btnExportExcel", { ns: "common" })}
        isExporting={handleExportExcel.isExporting}
        hasExportRole={hasRole(U3141ROLES.EXPORT)}
      />
    </>
  );
};

export default U3141LandRegisterList;
